// Mock Health Data for the HealthChoices AI Platform

// Wearable device integrations (mock data)
export const wearableDevices = [
  { id: 'apple_health', name: 'Apple Health', icon: '🍎', connected: true, lastSync: '2 mins ago' },
  { id: 'oura', name: 'Oura Ring', icon: '💍', connected: true, lastSync: '15 mins ago' },
  { id: 'whoop', name: 'WHOOP', icon: '⌚', connected: false, lastSync: null },
  { id: 'garmin', name: 'Garmin', icon: '🏃', connected: false, lastSync: null },
  { id: 'fitbit', name: 'Fitbit', icon: '💪', connected: true, lastSync: '1 hour ago' }
];

// Vitality Score Engine Data
export const vitalityScore = {
  current: 78,
  trend: '+3',
  subscores: [
    { name: 'Sleep', score: 82, trend: '+5', color: '#8B5CF6' },
    { name: 'Activity', score: 75, trend: '+2', color: '#22C55E' },
    { name: 'Nutrition', score: 71, trend: '-1', color: '#F59E0B' },
    { name: 'Recovery', score: 84, trend: '+4', color: '#3B82F6' },
    { name: 'Stress', score: 68, trend: '+2', color: '#EF4444' }
  ],
  history: [
    { date: 'Mon', score: 72 },
    { date: 'Tue', score: 74 },
    { date: 'Wed', score: 71 },
    { date: 'Thu', score: 76 },
    { date: 'Fri', score: 75 },
    { date: 'Sat', score: 78 },
    { date: 'Sun', score: 78 }
  ]
};

// Biological Age Engine Data
export const biologicalAge = {
  chronologicalAge: 35,
  biologicalAge: 31,
  difference: -4,
  longevityScore: 85,
  trend: 'improving',
  history: [
    { month: 'Jan', age: 34 },
    { month: 'Feb', age: 33 },
    { month: 'Mar', age: 33 },
    { month: 'Apr', age: 32 },
    { month: 'May', age: 32 },
    { month: 'Jun', age: 31 },
    { month: 'Jul', age: 31 }
  ]
};

// Health Risk Radar Data
export const healthRiskRadar = {
  metabolic: { risk: 'low', score: 85, factors: ['Healthy glucose levels', 'Good lipid profile'] },
  stressRecovery: { risk: 'moderate', score: 65, factors: ['Elevated cortisol', 'Good HRV variability'] },
  inflammation: { risk: 'low', score: 82, factors: ['Normal hs-CRP', 'Anti-inflammatory diet'] },
  lifestyle: { risk: 'low', score: 78, factors: ['Regular exercise', 'Adequate sleep'] }
};

// AI Digital Twin Predictions
export const digitalTwinPredictions = [
  {
    id: 1,
    scenario: 'Current Path',
    prediction: 'Maintaining current lifestyle will improve biological age by 2 years over the next 12 months.',
    confidence: 85,
    impact: 'positive'
  },
  {
    id: 2,
    scenario: 'Add 30min Daily Walk',
    prediction: 'Adding daily walking could reduce cardiovascular risk by 15% and improve sleep quality by 20%.',
    confidence: 78,
    impact: 'positive'
  },
  {
    id: 3,
    scenario: 'Reduce Stress',
    prediction: 'Implementing stress management could lower inflammation markers by 25% within 3 months.',
    confidence: 72,
    impact: 'positive'
  }
];

// AI Insights & Prescriptions
export const aiInsights = {
  dailyHealthPlan: [
    { time: '7:00 AM', action: 'Morning hydration - 500ml water', type: 'hydration', completed: true },
    { time: '7:30 AM', action: '15-min morning stretch routine', type: 'movement', completed: true },
    { time: '12:00 PM', action: 'Anti-inflammatory lunch: salmon + leafy greens', type: 'nutrition', completed: false },
    { time: '3:00 PM', action: '5-min breathing exercise', type: 'stress', completed: false },
    { time: '6:00 PM', action: '30-min moderate cardio', type: 'movement', completed: false },
    { time: '9:00 PM', action: 'Wind-down routine - no screens', type: 'sleep', completed: false }
  ],
  coachingInsights: [
    { id: 1, priority: 'high', message: 'Your sleep efficiency dropped 12% this week. Consider earlier bedtime.', category: 'Sleep' },
    { id: 2, priority: 'medium', message: 'Great job on hitting your step goal 5 days in a row!', category: 'Activity' },
    { id: 3, priority: 'low', message: 'Vitamin D levels are optimal. Maintain current supplementation.', category: 'Nutrition' }
  ],
  alerts: [
    { id: 1, type: 'warning', message: 'Resting heart rate elevated 8% above baseline', time: '2 hours ago' }
  ]
};

// Clinical/Lab Data
export const clinicalData = {
  glucose: { value: 95, unit: 'mg/dL', status: 'normal', range: '70-100' },
  hba1c: { value: 5.4, unit: '%', status: 'normal', range: '< 5.7' },
  insulin: { value: 8, unit: 'μU/mL', status: 'normal', range: '2-25' },
  lipids: {
    totalCholesterol: { value: 185, unit: 'mg/dL', status: 'normal' },
    ldl: { value: 95, unit: 'mg/dL', status: 'normal' },
    hdl: { value: 62, unit: 'mg/dL', status: 'good' },
    triglycerides: { value: 120, unit: 'mg/dL', status: 'normal' }
  },
  hsCRP: { value: 0.8, unit: 'mg/L', status: 'low', range: '< 1.0' },
  vitaminD: { value: 45, unit: 'ng/mL', status: 'optimal', range: '30-100' }
};

// Manual Input Tracking
export const manualInputs = {
  mood: { current: 'good', history: ['good', 'great', 'okay', 'good', 'great', 'good', 'good'] },
  stress: { current: 4, max: 10, history: [5, 6, 4, 5, 3, 4, 4] },
  hydration: { current: 6, goal: 8, unit: 'glasses', history: [7, 6, 8, 5, 7, 8, 6] },
  sleep: { hours: 7.5, quality: 'good', history: [7, 6.5, 8, 7, 7.5, 8, 7.5] },
  weight: { current: 165, unit: 'lbs', history: [168, 167, 166, 166, 165, 165, 165] },
  symptoms: []
};

// Program Data
export const programData = {
  activeProgram: 'Metabolic Reset',
  progress: 65,
  daysCompleted: 18,
  totalDays: 28,
  challenges: [
    { id: 1, name: '10K Steps Challenge', progress: 80, daysLeft: 5 },
    { id: 2, name: 'Sugar-Free Week', progress: 100, daysLeft: 0, completed: true }
  ],
  adherence: 85,
  lessonsCompleted: 12,
  totalLessons: 20
};

// Metrics Time Series (last 7 days)
export const metricsTimeSeries = {
  heartRate: [
    { date: 'Mon', resting: 62, average: 75, max: 145 },
    { date: 'Tue', resting: 64, average: 78, max: 152 },
    { date: 'Wed', resting: 61, average: 72, max: 138 },
    { date: 'Thu', resting: 63, average: 76, max: 148 },
    { date: 'Fri', resting: 60, average: 74, max: 142 },
    { date: 'Sat', resting: 59, average: 71, max: 135 },
    { date: 'Sun', resting: 61, average: 73, max: 140 }
  ],
  steps: [
    { date: 'Mon', count: 8542 },
    { date: 'Tue', count: 12350 },
    { date: 'Wed', count: 6890 },
    { date: 'Thu', count: 10234 },
    { date: 'Fri', count: 11567 },
    { date: 'Sat', count: 15230 },
    { date: 'Sun', count: 9845 }
  ],
  hrv: [
    { date: 'Mon', value: 45 },
    { date: 'Tue', value: 42 },
    { date: 'Wed', value: 48 },
    { date: 'Thu', value: 44 },
    { date: 'Fri', value: 50 },
    { date: 'Sat', value: 52 },
    { date: 'Sun', value: 49 }
  ]
};

export default {
  wearableDevices,
  vitalityScore,
  biologicalAge,
  healthRiskRadar,
  digitalTwinPredictions,
  aiInsights,
  clinicalData,
  manualInputs,
  programData,
  metricsTimeSeries
};
